import { selectCurrentHotelName } from 'accessors'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import FolderEmpty from 'components/ReusableComponents/Icons/FolderEmpty'
import FolderFilled from 'components/ReusableComponents/Icons/FolderFilled'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import { parentChainNames } from 'main/hotelBrandsAndChains'
import { useEffect, useState } from 'react'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import subsidiariesApi from 'services/api/subsidiaries'

import styles from './RateCodes.module.scss'

const columnNames = {
  DirectfulCode: 'Directful',
  OTA: 'OTA',
  FriendsAndFamily: 'Friends & family',
  Complimentary: 'Complimentary',
  DiscountClub: 'Discount club (AAA, ARP, etc.)',
  CorporateLocal: 'Corporate local',
  GovernmentLocal: 'Government local',
  RewardPointsClaim: 'Reward points',
  LongTermResident: 'Long term resident',
  DiscountLocal: 'Discount local',
  DiscountBrand: 'Discount brand',
  Unknown: 'Uncategorized'
}

const Category = ({ id, codes, isCollapsedByDefault, isCollapsable }) => {
  const [isCollapsed, setIsCollapsed] = useState(isCollapsedByDefault)

  return (
    <Droppable droppableId={id}>
      {provided => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={styles.category}
        >
          <div
            className={styles.categoryTitle}
            onClick={() => (isCollapsable ? setIsCollapsed(c => !c) : null)}
            style={{ cursor: isCollapsable ? 'pointer' : 'default' }}
          >
            {id === 'Unknown' ? (
              <FolderEmpty />
            ) : (
              <FolderFilled
                fill={id === 'DirectfulCode' ? 'rgb(155, 81, 224)' : '#A8ADB2'}
              />
            )}{' '}
            {columnNames[id]} ({codes && codes.length})
            {isCollapsable && (
              <button className={styles.collapseButton}>
                <svg
                  width="1em"
                  height="1em"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 10l-1.41-1.41L12 13.17 7.41 8.59 6 10l6 6 6-6z"
                    fill="#A8ADB2"
                    style={{
                      transform: isCollapsed ? 'rotate(0)' : 'rotate(180deg)',
                      transformOrigin: 'center'
                    }}
                  />
                </svg>
              </button>
            )}
          </div>
          {!isCollapsed && (
            <>
              <div
                className={
                  codes && codes.length > 0
                    ? styles.categoryInner
                    : styles.categoryInnerEmpty
                }
                style={{
                  borderColor: id === 'Directful' ? 'rgba(155, 81, 224, 0.4)' : null,
                  borderWidth: id === 'Directful' ? '2px' : null
                }}
              >
                {codes?.length > 0 &&
                  codes.map((item, index) => (
                    <RateCard key={item.code} item={item} index={index} />
                  ))}
                {provided.placeholder}
              </div>
            </>
          )}
        </div>
      )}
    </Droppable>
  )
}

const RateCard = ({ item, index }) => (
  <Draggable key={item.code} draggableId={item.code} index={index}>
    {(provided, snapshot) => (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className={snapshot.isDragging ? styles.rateCardDragging : styles.rateCard}
      >
        <span className={styles.rateCode}>{item.code}</span>
        <span className={styles.bookingsCount}>{item.bookingsCount} bookings</span>
      </div>
    )}
  </Draggable>
)

const RateCodes = () => {
  const parentChainCode = useSelector(s => s.hotelDetails.parentChainCode)
  const shouldDisplayParentChainTab = parentChainCode !== 'independent'

  const [isLoading, setIsLoading] = useState(false)
  const [isErred, setIsErred] = useState(false)
  const [tab, setTab] = useState('hotelLevel')
  const [showUnchangesWarningDialog, setShowUnchangesWarningDialog] = useState(false)

  const [rateState, setRateState] = useState([])

  const [changes, setChanges] = useState([])

  const currentHotelName = useSelector(selectCurrentHotelName)

  const history = useHistory()

  useEffect(() => {
    setIsLoading(true)
    subsidiariesApi.rateCodes
      .get({ isSubsidiaryLevel: tab === 'hotelLevel' })
      .then(res => {
        setRateState(res)
      })
      .catch(() => {
        setIsErred(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [tab])

  function onSave() {
    if (changes.length === 0) {
      return
    }

    setIsLoading(true)

    subsidiariesApi.rateCodes
      .put({ rateCodes: changes })
      .then(() => {
        history.push('/hotelDetails')
      })
      .catch(() => {
        setIsErred(true)
      })
  }

  const onDragEnd = ({ source, destination }) => {
    if (!destination) {
      return
    }

    if (source.droppableId === destination.droppableId) {
      const items = [...rateState[source.droppableId]]
      const [removed] = items.splice(source.index, 1)
      items.splice(destination.index, 0, removed)

      setRateState({
        ...rateState,
        [source.droppableId]: items
      })
    }

    if (source.droppableId !== destination.droppableId) {
      const sourceItems = [...rateState[source.droppableId]]
      const destItems = [...rateState[destination.droppableId]]
      const [removed] = sourceItems.splice(source.index, 1)

      setChanges(c => [
        ...c,
        {
          isSubsidiaryLevel: tab === 'hotelLevel',
          code: removed.code,
          kind: destination.droppableId
        }
      ])

      destItems.splice(destination.index, 0, removed)

      setRateState({
        ...rateState,
        [source.droppableId]: sourceItems,
        [destination.droppableId]: destItems
      })
    }
  }

  const handleTabChange = tab => {
    if (changes.length > 0) {
      setShowUnchangesWarningDialog(true)
    } else {
      setTab(tab)
    }
  }

  const handleUnsavedChangesSubmit = () => {
    setChanges([])
    setShowUnchangesWarningDialog(false)
    setTab(prev =>
      prev === 'hotelLevel' ? parentChainNames[parentChainCode] : 'hotelLevel'
    )
  }

  return (
    <>
      <ConfirmationDialog
        maxWidth={1000}
        title={`Rate codes categorization of ${currentHotelName}`}
        theme="blacklist"
        positiveMessage="Save changes"
        negativeMessage="Cancel"
        contentPadding="0"
        onReject={() => history.push('/hotelDetails')}
        onConfirm={onSave}
      >
        {isErred && <div className={styles.error}>Something went wrong</div>}
        {!isErred && (
          <div className={styles.wrapper}>
            {isLoading ? (
              <LoadingIndicator />
            ) : (
              <DragDropContext onDragEnd={onDragEnd}>
                <div className={styles.filterContainer}>
                  <div className={styles.tabs}>
                    <button
                      className={
                        tab === 'hotelLevel' ? styles.activeTab : styles.emptyTab
                      }
                      onClick={() =>
                        shouldDisplayParentChainTab ? handleTabChange('hotelLevel') : null
                      }
                    >
                      Hotel level
                    </button>
                    {shouldDisplayParentChainTab && (
                      <button
                        className={
                          tab !== 'hotelLevel' ? styles.activeTab : styles.emptyTab
                        }
                        onClick={() => handleTabChange(parentChainNames[parentChainCode])}
                      >
                        {`${parentChainNames[parentChainCode]} brand level`}
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex' }}>
                  <div className={styles.column}>
                    <Category id="DirectfulCode" codes={rateState.DirectfulCode} />
                    <Category
                      id="OTA"
                      codes={rateState.OTA}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="FriendsAndFamily"
                      codes={rateState.FriendsAndFamily}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="Complimentary"
                      codes={rateState.Complimentary}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="DiscountClub"
                      codes={rateState.DiscountClub}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="CorporateLocal"
                      codes={rateState.CorporateLocal}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="GovernmentLocal"
                      codes={rateState.GovernmentLocal}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="RewardPointsClaim"
                      codes={rateState.RewardPointsClaim}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="LongTermResident"
                      codes={rateState.LongTermResident}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="DiscountBrand"
                      codes={rateState.DiscountBrand}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="DiscountLocal"
                      codes={rateState.DiscountLocal}
                      isCollapsable
                      isCollapsedByDefault
                    />
                  </div>
                  <div className={styles.column}>
                    <Category id="Unknown" codes={rateState.Unknown} />
                  </div>
                </div>
              </DragDropContext>
            )}
          </div>
        )}
      </ConfirmationDialog>
      {showUnchangesWarningDialog && (
        <ConfirmationDialog
          title="Unsaved changes"
          positiveMessage="Discard"
          negativeMessage="Go back"
          theme="blacklist"
          onConfirm={handleUnsavedChangesSubmit}
          onClose={() => setShowUnchangesWarningDialog(false)}
          onPressEnter={handleUnsavedChangesSubmit}
        >
          <div>You've unsaved changes. Are you sure you want to leave?</div>
        </ConfirmationDialog>
      )}
    </>
  )
}

export default RateCodes
